import {Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {combineLatest} from 'rxjs';
import {AuthorizedObject} from '../../../api/dto';
import {Permission, PermissionAssignment, PermissionObjectType, PermissionSubjectType} from 'smag-commons/dto/api';
import {PermissionsService} from '../../../shared/permissions.service';
import {I18NService} from '../../../i18n/i18n.service';
import {MatDialog} from '@angular/material/dialog';
import {MsgBoxService} from '../../../shared/msg-box.service';
import {map} from 'rxjs/operators';
import {PermissionAssignmentObject} from '../new-permission-assignment/new-permission-assignment.component';
import {ALL_CONNECTOR_PERMISSION_PATTERNS, can} from '../../../shared/permissions';
import * as _ from 'lodash';
import {MatSelectChange} from '@angular/material/select';
import {AssignmentSubject} from '../permission-assignments/permission-assignments.component';
import {SmagApiService} from '../../../api/smag-api.service';
import {
    PatternPermissionSettings,
    PermissionMode,
    PermissionSettings
} from '../permission-list/permission-list.component';

interface Connector {
    id: string;
}

@Component({
    selector: 'app-perm-admin-connectors',
    templateUrl: './perm-admin-connectors.component.html',
    styleUrls: ['./perm-admin-connectors.component.scss']
})
export class PermAdminConnectorsComponent implements OnInit {

    @ViewChild('subjectAssignments') private subjectAssignmentDialog: TemplateRef<any>;
    @ViewChild('newAssignment') private newAssignmentDialog: TemplateRef<any>;

    connectors: Connector[];
    connector: Connector; // id is `null` for "all connectors"
    assignments: AuthorizedObject<PermissionAssignment>[];

    constructor(private readonly permissionsService: PermissionsService,
                private readonly apiService: SmagApiService,
                private readonly i18n: I18NService,
                private readonly dialog: MatDialog,
                private readonly msgBoxService: MsgBoxService) {
        this.connectors = null;
        this.connector = null;
        this.assignments = null;
    }

    ngOnInit() {
        combineLatest([
            this.apiService.getConnectorPermissions(),
            this.apiService.getAllPermittedConnectors()
        ]).pipe(
            map(([allConnectorsPerms, permConnectors]) => [
                ...(can(allConnectorsPerms.permissions, Permission.PERMISSIONS) ? [null] : []),
                ...(_.sortBy(Object.keys(permConnectors).filter(id => can(permConnectors[id], Permission.PERMISSIONS)), id => id))
            ]),
        ).subscribe(connectors => {
            this.connectors = connectors.map(id => ({
                id
            } as Connector));
        });
    }

    onClickNewAssignment() {
        this.dialog.open(this.newAssignmentDialog, {
            hasBackdrop: true,
            data: { // bound to $implicit
                subjectType: PermissionSubjectType.USER, // NOTE: only USER subjects currently supported
                object: {
                    type: PermissionObjectType.CONNECTOR,
                    id: this.connector.id,
                    permissionSettings: this.mkPermissionSettings(),
                    hint: this.connector.id ?
                        this.i18n.translate('connector {0}', this.connector.id) :
                        this.i18n.translate('connectors')
                } as PermissionAssignmentObject,
            }
        }).afterClosed().subscribe((assignment: PermissionAssignment) => {
            if (assignment) {
                this.addAssignment(assignment)
                    .then(() => {
                        this.msgBoxService.info(this.i18n.translate('Permissions added'));
                        this.reloadAssignments();
                    })
                    .catch(err => {
                        this.msgBoxService.error(this.i18n.translate('Cannot add permissions'), err);
                    });
            }
        });
    }

    onSelectConnector(event: MatSelectChange) {
        this.connector = event.value as Connector;
        this.reloadAssignments();
    }

    onClickSubjectAssignments(subject: AssignmentSubject) {
        this.dialog.open(this.subjectAssignmentDialog, {
            hasBackdrop: true,
            data: { // bound to $implicit
                subjectType: subject.type,
                subjectId: subject.id,
                hint: subject.hint
            }
        });
    }

    private reloadAssignments() {
        this.assignments = null; // trigger loading spinner
        this.permissionsService.getPermissionsForObject(PermissionObjectType.CONNECTOR, this.connector.id).subscribe(assignments => {
            this.assignments = assignments;
        });
    }

    private addAssignment(assignment: PermissionAssignment): Promise<void> {
        return this.permissionsService.assignPermissions(assignment);
    }

    private mkPermissionSettings(): PermissionSettings {
        return {
            mode: PermissionMode.PATTERN,
            allPatterns: ALL_CONNECTOR_PERMISSION_PATTERNS
        } as PatternPermissionSettings;
    }
}
